import type { AppType } from "@liftquest/api/client";
import { hc, type ClientResponse, type InferResponseType } from "hono/client";

/** Typed client for the LiftQuest API (same origin: Vite proxy in dev, nginx in production). */
export const client = hc<AppType>("/", { init: { credentials: "same-origin" } });
export const api = client.api;

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

type OkBody<T> = T extends ClientResponse<infer O, infer S, "json"> ? (S extends 200 | 201 ? O : never) : never;

/** Awaits a client call, throws ApiError for non-2xx responses and returns the typed JSON body. */
export async function call<T extends ClientResponse<unknown, number, "json">>(request: Promise<T>): Promise<OkBody<T>> {
  const res = await request;
  if (!res.ok) {
    let message = res.statusText;
    try {
      message = ((await res.json()) as { error?: string }).error ?? message;
    } catch {
      // not JSON
    }
    throw new ApiError(res.status, message);
  }
  return (await res.json()) as OkBody<T>;
}

export type Dashboard = InferResponseType<typeof api.dashboard.$get, 200>;
export type Me = InferResponseType<typeof api.me.$get, 200>;
export type PlanFull = InferResponseType<(typeof api.plans)[":id"]["$get"], 200>;
export type PlanList = InferResponseType<typeof api.plans.$get, 200>;
export type Exercise = InferResponseType<typeof api.exercises.$get, 200>[number];
export type ExerciseDetail = InferResponseType<(typeof api.exercises)[":id"]["$get"], 200>;
export type ExerciseHistory = InferResponseType<(typeof api.exercises)[":id"]["history"]["$get"], 200>;
export type TrainingData = InferResponseType<(typeof api.sessions)[":id"]["training"]["$get"], 200>;
export type SessionDetail = InferResponseType<(typeof api.sessions)[":id"]["$get"], 200>;
export type Progress = InferResponseType<typeof api.progress.$get, 200>;
export type FinishResult = InferResponseType<(typeof api.sessions)[":id"]["finish"]["$post"], 200>;
